import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { ROUTES } from '../../constants/routes';
import logo from '../../assets/img/tl-logo.png';
import { UI, DISPLAY, EASE, chip } from '../../styles/tl5';

// The full-bleed split both auth screens sit in: a dark art panel on the
// left, the form on the right. Login and Signup share it rather than each
// drawing their own, so the two can never drift apart.
//
// The art panel is decoration — it is hidden below `md` (where it would
// push the form off-screen) and marked aria-hidden so a screen reader
// goes straight to the form.
const STEPS = ['Book', 'Collect', 'Lab', 'Report'];
const BADGES = ['NABL', 'ISO 15189', 'ICMR'];

const AuthShell = ({ children }) => (
  <Box sx={{ display: 'flex', minHeight: '100vh', background: '#fff' }}>
    <Box
      component="aside"
      aria-hidden="true"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        flex: '1 1 50%',
        padding: 'clamp(32px, 4vw, 56px)',
        background: 'var(--tl-g9)',
        color: '#fff',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px),'
            + 'linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          pointerEvents: 'none',
        },
        // The drifting dashed ring in the lower right.
        '&::after': {
          content: '""',
          position: 'absolute',
          right: '-20%',
          bottom: '-28%',
          width: 440,
          height: 440,
          borderRadius: '50%',
          border: '1px dashed rgba(95,224,198,.35)',
          animation: `tl-spin 60s linear infinite`,
          pointerEvents: 'none',
        },
      }}
    >
      <Box component={Link} to={ROUTES.HOME} aria-label="TrustLab home" sx={{ position: 'relative', display: 'block', width: 'fit-content' }}>
        <Box component="img" src={logo} alt="TrustLab Diagnostics" sx={{ width: 168, height: 'auto', display: 'block' }} />
      </Box>

      <Box sx={{ position: 'relative', maxWidth: 420 }}>
        <Box
          component="p"
          sx={{
            m: 0,
            fontFamily: DISPLAY,
            fontSize: 'clamp(38px, 3.6vw, 54px)',
            fontWeight: 700,
            lineHeight: 1.08,
            letterSpacing: '-.035em',
            textWrap: 'balance',
          }}
        >
          Every report, signed by a doctor.
        </Box>

        {/* The four stages of a booking, with the last one lit. */}
        <Box sx={{ position: 'relative', mt: '34px' }}>
          <Box sx={{ position: 'relative', height: 2, background: 'repeating-linear-gradient(90deg, rgba(255,255,255,.35) 0 8px, transparent 8px 16px)' }}>
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                width: 14,
                height: 14,
                mt: '-7px',
                borderRadius: '50%',
                background: 'var(--tl-teal-l)',
                boxShadow: '0 0 0 6px rgba(95,224,198,.18)',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '12px' }}>
            {STEPS.map((s, i) => (
              <Box
                key={s}
                component="span"
                sx={{
                  fontFamily: UI,
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: '.16em',
                  textTransform: 'uppercase',
                  color: i === STEPS.length - 1 ? 'var(--tl-teal-l)' : 'rgba(255,255,255,.55)',
                  transition: `color .4s ${EASE}`,
                }}
              >
                {s}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {BADGES.map((b) => (
          <Box key={b} component="span" sx={{ ...chip, background: 'rgba(255,255,255,.1)', color: '#fff' }}>
            {b}
          </Box>
        ))}
      </Box>
    </Box>

    <Box
      component="main"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: '1 1 50%',
        minWidth: 0,
        padding: 'clamp(28px, 5vw, 64px) clamp(20px, 5vw, 72px)',
      }}
    >
      {/* On a phone the art panel is gone, so the logo moves in here. */}
      <Box
        component={Link}
        to={ROUTES.HOME}
        aria-label="TrustLab home"
        sx={{ display: { xs: 'block', md: 'none' }, width: 'fit-content', mb: '28px' }}
      >
        <Box component="img" src={logo} alt="TrustLab Diagnostics" sx={{ width: 148, height: 'auto', display: 'block' }} />
      </Box>

      <Box sx={{ width: '100%', maxWidth: 440, mx: { xs: 0, md: 'auto' } }}>{children}</Box>
    </Box>
  </Box>
);

export default AuthShell;
